import * as React from "react"
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import "../../../assets/scss/RandomSchedulingExclusions.scss"
import DaysToExclude from "../../../api/Randoms/GovPoolModel/DaysToExclude"
import { parseISO, format } from "date-fns"
import { Button, Col, Container, Form, Modal, ModalDialog, Row, Spinner } from "react-bootstrap"
import Checkbox from "../../Checkbox"
import { ChangeEvent } from "react"
import ConfirmationModal from "../../ConfirmationModal"
import { Subscription } from "rxjs"
import { toast } from "react-toastify"
import GovPoolCheckTestsExistObservable from "../../../api/Randoms/GovPoolCheckTestsExist"
import Draggable from 'react-draggable'

export interface DaysToExcludeExt extends DaysToExclude {
  removed?: boolean
  DateExcludeDt?: number
}

interface RandomSchedulingExclusionsModalProps {
  poolID: number
  daysToExclude: DaysToExcludeExt[]
  onCancel: () => void
  onSave: (list: DaysToExcludeExt[]) => void
}

interface RandomSchedulingExclusionsModalState {
  daysToExclude: DaysToExcludeExt[]
  weekdaysToExclude: number[]
  selectedDate?: Date
  selectedDayToExclude?: DaysToExcludeExt
  selectedDayDescription?: string
  selectedDayGender?: string
  selectedDayPoolID?: number
  showRemoveExistingEventsConfirmation: boolean
  showAllGroupsConfirmation: boolean
  dateCheckInProgress: boolean
}

class DraggableModalDialog extends React.Component {
  render() {
      return <Draggable handle=".modal-header"><ModalDialog {...this.props} /> 
 </Draggable>
  }
}

function buildDaysToExcludeList(daysToExclude: DaysToExcludeExt[]) {
  const parseISODate = (date: string) => {
    const parsed = parseISO(date)
    if (parsed) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
    }
    return undefined
  }

  return daysToExclude.map(item => ({
    ...item,
    DateExcludeDt: item.DateExcludeDt || (item.DateExclude ? parseISODate(item.DateExclude!)?.getTime() : undefined)
  } as DaysToExcludeExt))
}

function buildWeekdaysToExcludeList(daysToExclude: DaysToExcludeExt[]) {
  return daysToExclude.filter(item => item.DayOfWeekID && !item.removed).map(item => item.DayOfWeekID!)
}

export default class RandomSchedulingExclusionsModal extends React.PureComponent<RandomSchedulingExclusionsModalProps, RandomSchedulingExclusionsModalState> {
  state: RandomSchedulingExclusionsModalState = {
    daysToExclude: buildDaysToExcludeList(this.props.daysToExclude),
    weekdaysToExclude: buildWeekdaysToExcludeList(this.props.daysToExclude),
    showRemoveExistingEventsConfirmation: false,
    showAllGroupsConfirmation: false,
    dateCheckInProgress: false
  }
  dateToAdd?: Date
  checkTestEventSubscription?: Subscription

  componentWillUnmount() {
    this.checkTestEventSubscription && this.checkTestEventSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: RandomSchedulingExclusionsModalProps) {
    if (this.props.daysToExclude !== prevProps.daysToExclude) {
      this.setState({
        daysToExclude: buildDaysToExcludeList(this.props.daysToExclude),
        weekdaysToExclude: buildWeekdaysToExcludeList(this.props.daysToExclude)
      })
    }
  }

  handleExclusionDateDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (this.state.selectedDayToExclude) {
      this.state.selectedDayToExclude.Description = value
    }
    this.setState({ selectedDayDescription: value })
  }

  handleExclusionDateGenderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked ? e.target.value : undefined

    if (this.state.selectedDayToExclude) {
      this.state.selectedDayToExclude.Gender = value
    }
    this.setState({ selectedDayGender: value })
  }

  handleExclusionDateAllGroupsChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      this.setState({ showAllGroupsConfirmation: true })
    } else {
      this.setAllGroups(false)
    }
  }

  setAllGroups = (checked: boolean) => {
    const value = checked ? undefined : this.props.poolID

    if (this.state.selectedDayToExclude) {
      this.state.selectedDayToExclude.PoolID = value
    }
    this.setState({
      selectedDayPoolID: value,
      showAllGroupsConfirmation: false
    })
  }

  handleDateEvent = (date: Date | [Date, Date] | null, event: React.SyntheticEvent<any> | undefined) => {
    if (!date || !(date instanceof Date)) return
    if (!event) {
      // Keyboard move
      let dayToExclude = this.getDayToExclude(date)
      this.setState({
        selectedDate: date,
        selectedDayToExclude: dayToExclude,
        selectedDayDescription: dayToExclude?.Description,
        selectedDayGender: dayToExclude?.Gender,
        selectedDayPoolID: dayToExclude?.PoolID
      })
    } else if (event.type === 'click') {
      // Mouse click
      this.toggleDayExclusion(date)
    } else if (event.type === 'keydown' && event.nativeEvent instanceof KeyboardEvent && (event.nativeEvent as KeyboardEvent).code === "Space") {
      // Space key down
      this.toggleDayExclusion(date)
    }
  }

  toggleDayExclusion = (date: Date) => {
    // Do not handle dates covered by weekday exclusion
    if (this.state.weekdaysToExclude.includes(date.getDay() + 1)) return

    let dayToExclude = this.getDayToExclude(date)
    if (!dayToExclude) {
      this.dateToAdd = date
      this.setState({ dateCheckInProgress: true })
      this.checkTestEventSubscription = GovPoolCheckTestsExistObservable(this.props.poolID, date).subscribe(
        result => result
          ? this.setState({ showRemoveExistingEventsConfirmation: true, dateCheckInProgress: false })
          : this.addDayToExclude(),
        error => {
          this.setState({ dateCheckInProgress: false })
          toast.error(error?.errorData?.message || error.message)
        }
      )
      return
    }
    if (this.state.selectedDate?.getTime() !== date.getTime()) {
      // Click on non-selected exclusion day - just select this day
      this.setState({
        selectedDate: date,
        selectedDayToExclude: dayToExclude,
        selectedDayDescription: dayToExclude.Description,
        selectedDayGender: dayToExclude.Gender,
        selectedDayPoolID: dayToExclude.PoolID
      })
      return
    }

    // Click on already selected exclusion date - remove the exclusion date
    dayToExclude.removed = true

    this.setState({
      daysToExclude: [...this.state.daysToExclude],
      selectedDate: date,
      selectedDayToExclude: undefined,
      selectedDayDescription: undefined,
      selectedDayGender: undefined,
      selectedDayPoolID: undefined
    })
  }

  addDayToExclude = () => {
    // Click on day without exclusion date - Add exclusion date
    if (this.dateToAdd) {
      const dayToExclude: DaysToExcludeExt = {
        DaysToExcludeID: 0,
        ImplementationID: 0,
        AccountID: 0,
        PoolID: this.props.poolID,
        DateExclude: format(this.dateToAdd, "yyyy-MM-dd'T'HH:mm:ss"),
        DateExcludeDt: new Date(this.dateToAdd.getFullYear(), this.dateToAdd.getMonth(), this.dateToAdd.getDate()).getTime()
      }
      this.dateToAdd = undefined
      this.setState({
        selectedDate: this.dateToAdd,
        daysToExclude: [...this.state.daysToExclude, dayToExclude],
        selectedDayToExclude: dayToExclude,
        selectedDayDescription: dayToExclude.Description,
        selectedDayGender: dayToExclude.Gender,
        selectedDayPoolID: dayToExclude.PoolID,
        showRemoveExistingEventsConfirmation: false,
        dateCheckInProgress: false
      })
    } else {
      this.setState({
        showRemoveExistingEventsConfirmation: false,
        dateCheckInProgress: false
      })
    }
  }

  getDayToExclude = (date: Date) => {
    const dateSeconds = date.getTime()
    return this.state.daysToExclude.find(item => item.DateExcludeDt === dateSeconds && !item.removed)
  }

  render() {
    const datesExcluded: { [id: number]: DaysToExcludeExt } = Object.assign({}, ...this.state.daysToExclude.filter(item => item.DateExcludeDt).map((x) => ({ [x.DateExcludeDt!]: x })))

    const dayClassName = (date: Date) => {
      if (this.state.weekdaysToExclude.includes(date.getDay() + 1)) {
        return "excluded-day weekday"
      }
      const excludedDate = datesExcluded[date.getTime()]
      if (excludedDate && !excludedDate.removed) {
        return (excludedDate.PoolID ?? undefined) === undefined ? "excluded-day all-groups" : "excluded-day"
      }
      return null
    }

    const { onCancel, onSave } = this.props
    const classNameDisabled = this.state.selectedDayToExclude ? "" : " disabled"

    return <>
      <Modal dialogAs={DraggableModalDialog} backdrop="static" centered show onHide={onCancel} size="sm" className="RandomSchedulingExclusionsModal">
        <Modal.Header>
          <Modal.Title>Dates to Exclude</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="text-center legend">
              <Col>
                <Form.Label className="pr-2">This Group</Form.Label>
                <Form.Label className="legend-item this-group">&nbsp;</Form.Label>
              </Col>
              <Col>
                <Form.Label className="pr-2">All Groups</Form.Label>
                <Form.Label className="legend-item all-groups">&nbsp;</Form.Label>
              </Col>
            </Row>
            <Row className="text-center">
              <Col>
                <DatePicker inline
                  peekNextMonth
                  adjustDateOnChange
                  allowSameDay
                  minDate={new Date()}
                  enableTabLoop={false}
                  selected={this.state.selectedDate}
                  onChange={this.handleDateEvent}
                  dayClassName={(date) => dayClassName(date)}
                />
              </Col>
            </Row>
            <Row className="small">
              <Form.Label column className={`col-3${classNameDisabled}`} htmlFor="txtDescription">Description</Form.Label>
              <Col xs={9}>
                <Form.Control size="sm" type="text" id="txtDescription"
                  disabled={!this.state.selectedDayToExclude}
                  value={this.state.selectedDayDescription || ""}
                  onChange={this.handleExclusionDateDescriptionChange} />
              </Col>
            </Row>
            <Row className="small">
              <Form.Label column className={classNameDisabled}>Exclude by Gender</Form.Label>
              <Col>
                <Checkbox id="chkMale" className="abc-checkbox"
                  label="Male"
                  value="M"
                  disabled={!this.state.selectedDayToExclude}
                  checked={this.state.selectedDayGender === 'M'}
                  onChange={this.handleExclusionDateGenderChange} />
                <Checkbox id="chkFemale" className="abc-checkbox"
                  label="Female"
                  value="F"
                  disabled={!this.state.selectedDayToExclude}
                  checked={this.state.selectedDayGender === 'F'}
                  onChange={this.handleExclusionDateGenderChange} />
              </Col>
            </Row>
            <Row className="small">
              <Col>
                <Checkbox id="chkAllGroups" inline className="abc-checkbox"
                  label="Exclude For All Groups"
                  disabled={!this.state.selectedDayToExclude}
                  checked={this.state.selectedDayToExclude && (this.state.selectedDayPoolID ?? undefined) === undefined}
                  onChange={this.handleExclusionDateAllGroupsChange} />
              </Col>
            </Row>
          </Form>
          {this.state.dateCheckInProgress && <div className="loading-overlay"><Spinner animation="border" variant="light" /></div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={() => onSave(this.state.daysToExclude)}>Save</Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal show={this.state.showRemoveExistingEventsConfirmation}
        title="CONFIRM"
        confirmButtonText="Yes"
        cancelButtonText="No"
        onConfirm={() => this.addDayToExclude()}
        onHide={() => this.setState({ showRemoveExistingEventsConfirmation: false })}>
        <Container fluid>
          <Row>
            <Col>
              <h5>There are collections scheduled for this date!<br />If you proceed to exclude this date all pending collections will be cancelled.</h5>
              <p>Do you want to proceed?</p>
            </Col>
          </Row>
        </Container>
      </ConfirmationModal>
      <ConfirmationModal show={this.state.showAllGroupsConfirmation}
        title="CONFIRM"
        textTitle="This action will cancel all tests that are scheduled for this date, not just the tests associated with this group."
        text="Do you still wish to proceed?"
        showImage={false}
        confirmButtonText="Yes"
        cancelButtonText="No"
        onConfirm={() => this.setAllGroups(true)}
        onHide={() => this.setState({ showAllGroupsConfirmation: false })} />
    </>
  }
}
